import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_URL } from '../config';
import { ChevronRight } from '../components/icons';

const STEPS = [
  {
    num: '01',
    label: 'Open',
    headline: 'Start the day with a known cash position.',
    detail: ['Set opening float', 'System logs start time and cashier', 'POS is now open for sales'],
  },
  {
    num: '02',
    label: 'Sell',
    headline: 'Find any product in seconds.',
    detail: ['Search or browse products', 'Tap to add to cart', 'Adjust quantities inline'],
  },
  {
    num: '03',
    label: 'Charge',
    headline: 'Accept cash, card or transfer.',
    detail: ['Select payment method', 'Enter amount received', 'Change calculated for cash'],
  },
  {
    num: '04',
    label: 'Receipt',
    headline: 'Every sale is confirmed and filed.',
    detail: ['Receipt issued', 'Stock moves when tracked', 'Books updated'],
  },
  {
    num: '05',
    label: 'Close',
    headline: 'End every shift with a clear reconciliation.',
    detail: ['Count physical cash', 'System shows expected amount', 'Variance recorded'],
  },
];

export function PosPage() {
  const [step, setStep] = useState(0);

  return (
    <>
      <section className="bg-inverse px-6 pt-28 pb-16 lg:px-10 lg:pt-36 lg:pb-20">
        <div className="mx-auto grid max-w-[1280px] items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/20 px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
              cevop POS
            </div>
            <h1 className="font-display mb-5 text-[2.75rem] leading-[1.06] font-extrabold tracking-tight text-inverse-fg lg:text-[3.625rem]">
              Selling should be
              <br />
              the easy part.
            </h1>
            <p className="mb-8 max-w-[440px] text-[1.0625rem] leading-[1.7] text-inverse-fg/55">
              Sell quickly in POS. Run and understand the business in cevop Business. Open a register, take payment,
              close cleanly — every sale connects to stock and money without becoming another dashboard.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={SIGNUP_URL}
                className="inline-flex items-center justify-center rounded-xl bg-brand px-7 py-3.5 text-[0.9375rem] font-semibold text-brand-on transition-colors hover:bg-brand-hover"
              >
                Start free
              </a>
              <a
                href="#demo"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-inverse-fg/15 px-7 py-3.5 text-[0.9375rem] font-semibold text-inverse-fg transition-colors hover:bg-inverse-fg/5"
              >
                See the workflow
                <ChevronRight />
              </a>
            </div>
          </div>

          <div className="mk-float relative mx-auto max-w-[360px] lg:max-w-none">
            <div className="mk-device rounded-[2rem] border border-white/10 bg-zinc-950 p-5 shadow-2xl">
              <div className="overflow-hidden rounded-[1.5rem] border border-inverse-fg/[0.06] bg-surface">
                <div className="flex items-center justify-between border-b border-inverse-fg/[0.06] px-5 py-3">
                  <span className="font-mono-data text-[0.62rem] tracking-wider text-inverse-fg/35 uppercase">POS open</span>
                  <span className="font-mono-data text-[0.62rem] text-inverse-fg/30">14:22</span>
                </div>
                <div className="p-4">
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    {[
                      ['Rice 5kg', '₦12,500'],
                      ['Oil 2L', '₦4,200'],
                      ['Sugar 1kg', '₦1,800'],
                      ['Bread', '₦1,200'],
                    ].map(([name, price]) => (
                      <div key={name} className="rounded-xl border border-inverse-fg/[0.07] bg-panel/[0.04] p-3">
                        <div className="mb-0.5 text-[0.72rem] text-inverse-fg/70">{name}</div>
                        <div className="font-mono-data text-[0.78rem] font-bold text-brand">{price}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mb-3 rounded-xl border border-inverse-fg/[0.07] bg-panel/[0.04] p-3.5">
                    <div className="mb-2 flex justify-between text-[0.72rem]">
                      <span className="text-inverse-fg/40">Total</span>
                      <span className="font-display text-[1.2rem] font-extrabold text-inverse-fg">₦18,500</span>
                    </div>
                  </div>
                  <div className="w-full rounded-xl bg-brand py-3.5 text-center text-[0.875rem] font-bold text-brand-on">
                    Charge ₦18,500
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10 lg:py-28" id="demo">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-12 text-center">
            <p className="mb-3 text-[0.72rem] font-bold tracking-widest text-brand uppercase">The POS workflow</p>
            <h2 className="font-display mb-4 text-[2rem] leading-[1.1] font-extrabold text-ink lg:text-[2.625rem]">
              Five steps. Every shift.
            </h2>
          </div>
          <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="mk-stagger space-y-2">
              {STEPS.map((s, i) => (
                <button
                  key={s.num}
                  type="button"
                  onClick={() => setStep(i)}
                  className={`w-full rounded-2xl border p-5 text-left transition-all duration-200 ${
                    step === i
                      ? 'border-brand/30 bg-brand-light/60 shadow-sm'
                      : 'border-line bg-panel hover:border-brand/20'
                  }`}
                >
                  <div className={`font-display mb-1 text-[1rem] font-bold ${step === i ? 'text-brand' : 'text-ink'}`}>
                    {s.num} · {s.label}
                  </div>
                  <div className="text-[0.82rem] text-ink-3">{s.headline}</div>
                  {step === i && (
                    <ul className="mt-3 space-y-1">
                      {s.detail.map((d) => (
                        <li key={d} className="text-[0.75rem] text-ink-2">
                          · {d}
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
            <div className="mk-device rounded-[2.5rem] bg-zinc-950 p-5 shadow-2xl lg:sticky lg:top-24">
              <div key={STEPS[step].num} className="mk-panel min-h-[280px] rounded-[2rem] bg-surface p-5 text-ink">
                <div className="font-mono-data mb-3 text-[0.6rem] text-inverse-fg/30 uppercase">Step {STEPS[step].num}</div>
                <div className="font-display mb-2 text-lg font-bold">{STEPS[step].label}</div>
                <p className="text-[0.85rem] text-inverse-fg/55">{STEPS[step].headline}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-surface-2/60 px-6 py-20 lg:px-10 lg:py-24">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-12 text-center">
            <h2 className="font-display text-[2rem] leading-[1.1] font-extrabold text-ink lg:text-[2.5rem]">
              Everything the counter needs.
            </h2>
          </div>
          <div className="mk-stagger grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              ['Fast selling', 'Search any product instantly. Add to cart with a tap.'],
              ['All payment types', 'Cash with change, card, and bank transfer — each recorded on the ledger.'],
              ['POS control', 'Open with a declared float and close with expected vs counted cash.'],
              ['Receipts', 'Print from the browser on every completed sale.'],
              ['Returns and voids', 'Refunds and voids with an audit trail.'],
              ['Offline resilience', 'Keep selling through interruptions; records sync when connected.'],
            ].map(([title, desc]) => (
              <div key={title} className="mk-lift rounded-2xl border border-line bg-panel p-6 shadow-sm">
                <div className="font-display mb-2 text-[0.95rem] font-bold text-ink">{title}</div>
                <div className="text-[0.82rem] leading-[1.65] text-ink-3">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-inverse px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[640px] text-center">
          <h2 className="font-display mb-5 text-[2.25rem] leading-[1.08] font-extrabold text-inverse-fg lg:text-[2.75rem]">
            Start selling with cevop today.
          </h2>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={SIGNUP_URL}
              className="inline-flex items-center justify-center rounded-xl bg-brand px-8 py-3.5 text-[0.9375rem] font-semibold text-brand-on hover:bg-brand-hover"
            >
              Start free
            </a>
            <Link
              to="/business"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-inverse-fg/15 px-8 py-3.5 text-[0.9375rem] font-semibold text-inverse-fg hover:bg-inverse-fg/5"
            >
              See the Business app
              <ChevronRight />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
